// ===================================================================
// 📊 GERENCIADOR DE COTAS - Cadastro de tipos de cota
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { useNotifications } from '../../contexts/NotificationContext';

export const QuotaManager = React.memo(({ user }) => {
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuota, setEditingQuota] = useState(null);
  const [formData, setFormData] = useState({ code: '', description: '' });
  const [error, setError] = useState('');
  const { addNotification } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getQuotas(true);
      setQuotas(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erro ao carregar cotas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = useCallback((field, value) => {
    if (field === 'code') value = (value || '').toUpperCase();
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  }, [error]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const code = (formData.code || '').trim();
    const description = (formData.description || '').trim();
    if (!code || !description) {
      setError('Preencha abreviatura e descrição');
      return;
    }

    try {
      setLoading(true);
      if (editingQuota) {
        const { error: updateError } = await apiClient.updateQuota(editingQuota.id, { code, description });
        if (updateError) {
          setError(updateError);
        } else {
          addNotification('Sucesso', 'Cota atualizada com sucesso', 'success');
          setShowModal(false);
          setEditingQuota(null);
          loadData();
        }
      } else {
        const { error: createError } = await apiClient.createQuota({ code, description });
        if (createError) {
          setError(createError);
        } else {
          addNotification('Sucesso', 'Cota criada com sucesso', 'success');
          setShowModal(false);
          loadData();
        }
      }
    } catch (err) {
      setError('Erro ao salvar cota');
    } finally {
      setLoading(false);
    }
  }, [formData, editingQuota, loadData, addNotification]);

  const handleEdit = useCallback((quota) => {
    setEditingQuota(quota);
    setFormData({ code: quota.code || '', description: quota.description || '' });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (quotaId) => {
    if (!window.confirm('Tem certeza que deseja desativar esta cota?')) return;
    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deleteQuota(quotaId);
      if (deleteError) {
        setError(deleteError);
      } else {
        addNotification('Sucesso', 'Cota desativada com sucesso', 'success');
        loadData();
      }
    } catch (err) {
      setError('Erro ao desativar cota');
    } finally {
      setLoading(false);
    }
  }, [loadData, addNotification]);

  const handleNewQuota = useCallback(() => {
    setEditingQuota(null);
    setFormData({ code: '', description: '' });
    setShowModal(true);
  }, []);

  if (loading && quotas.length === 0) {
    return <LoadingSpinner text="Carregando cotas..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>📊 Cotas</h2>
          <button onClick={handleNewQuota} style={{ ...styles.button, ...styles.buttonPrimary }}>
            ➕ Nova Cota
          </button>
        </div>

        {error && !showModal && (
          <Alert message={error} type="error" onClose={() => setError('')} />
        )}

        {quotas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
            <h3>Nenhuma cota cadastrada</h3>
            <p>Cadastre as cotas (ex: AC - Ampla concorrência) para usar nos cursos</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Abreviatura</th>
                  <th style={styles.tableHeaderCell}>Descrição</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {quotas.map(q => (
                  <tr key={q.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <span style={{ fontWeight: '600', color: '#1F2937' }}>{q.code}</span>
                    </td>
                    <td style={styles.tableCell}>{q.description}</td>
                    <td style={styles.tableCell}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEdit(q)}
                          style={{ ...styles.button, ...styles.buttonSecondary, ...styles.buttonSmall }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleDelete(q.id)}
                          style={{ ...styles.button, ...styles.buttonDanger, ...styles.buttonSmall }}
                        >
                          🗑️ Desativar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editingQuota ? 'Editar Cota' : 'Nova Cota'}>
        {error && (
          <div style={{ marginBottom: '16px' }}>
            <Alert message={error} type="error" onClose={() => setError('')} />
          </div>
        )}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Abreviatura *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => handleInputChange('code', e.target.value)}
                style={styles.input}
                placeholder="Ex: AC"
                maxLength={20}
                required
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Descrição *</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                style={styles.input}
                placeholder="Ex: Ampla concorrência"
                required
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button type="button" onClick={() => setShowModal(false)} style={{ ...styles.button, ...styles.buttonSecondary }}>
              Cancelar
            </button>
            <button type="submit" disabled={loading} style={{ ...styles.button, ...styles.buttonPrimary, opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Salvando...' : (editingQuota ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
});
